/**
 * Planning Progress Component
 * Minimal horizontal stepper for video planning workflow
 * Shows 3 phases in a single, sleek horizontal flow with animated gradient lines
 * Uses time-based linear progress estimation for smooth UX
 */

import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { CheckCircle2, PenTool, Palette, FolderCheck, Loader2 } from 'lucide-react';
import { PlanningStatus } from '@/types/workflow';
import { useAgentJob } from '@/hooks/useAgentJob';

interface PlanningProgressProps {
  planningStatus: PlanningStatus;
}

/**
 * Estimated durations for each phase (in seconds)
 */
const ESTIMATED_DURATIONS = {
  story_architect: 60,    // 1 minute
  layout_designer: 60,    // 1 minute
  initiate_slides: 0,     // instant
} as const;

/**
 * Creator-friendly phase configuration
 */
const PHASE_CONFIG = {
  story_architect: {
    icon: PenTool,
    label: 'Writing Story',
    shortLabel: 'Writing',
  },
  layout_designer: {
    icon: Palette,
    label: 'Designing Layouts',
    shortLabel: 'Designing',
  },
  initiate_slides: {
    icon: FolderCheck,
    label: 'Setting Up Slides',
    shortLabel: 'Setup',
  },
} as const;

/**
 * Horizontal phase indicator with animated gradient line
 */
interface PhaseNodeProps {
  phase: keyof typeof PHASE_CONFIG;
  currentPhase: string;
  status: 'pending' | 'in_progress' | 'completed' | 'error';
  isLast?: boolean;
  lineProgress?: number; // 0-100, progress for this connecting line
}

function PhaseNode({ phase, currentPhase, status, isLast, lineProgress = 0 }: PhaseNodeProps) {
  const config = PHASE_CONFIG[phase];
  const Icon = config.icon;
  const isActive = currentPhase === phase;
  const isCompleted = status === 'completed';
  const isPending = status === 'pending';

  return (
    <div className="flex items-center flex-1">
      {/* Phase node */}
      <div className="flex flex-col items-center gap-1.5">
        {/* Icon circle */}
        <div
          className={cn(
            'relative w-8 h-8 rounded-full flex items-center justify-center border-2 transition-all duration-300',
            isCompleted && 'bg-green-100 border-green-500 dark:bg-green-900/30 dark:border-green-500',
            isActive && 'bg-accent border-primary',
            isPending && 'bg-gray-100 border-gray-300 dark:bg-gray-800 dark:border-gray-600'
          )}
        >
          {isCompleted ? (
            <CheckCircle2 className="w-4 h-4 text-green-600 dark:text-green-400" />
          ) : (
            <Icon
              className={cn(
                'w-4 h-4 transition-opacity duration-300',
                isActive && 'text-primary dark:text-accent-foreground',
                isPending && 'text-gray-400 dark:text-gray-500'
              )}
            />
          )}
        </div>

        {/* Label */}
        <span
          className={cn(
            'text-xs font-medium transition-opacity duration-300',
            isActive && 'text-primary dark:text-accent-foreground',
            isCompleted && 'text-green-600 dark:text-green-400',
            isPending && 'text-gray-500 dark:text-gray-500'
          )}
        >
          {config.shortLabel}
        </span>
      </div>

      {/* Connecting line with animated gradient */}
      {!isLast && (
        <div className="flex-1 h-1.5 mx-2 mt-[-18px] relative overflow-hidden rounded-full">
          {/* Background line */}
          <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700" />

          {/* Progress overlay - blue gradient when in progress, green when completed */}
          {lineProgress > 0 && (
            <div
              className={cn(
                'absolute inset-0 transition-all duration-500 ease-out',
                lineProgress >= 100
                  ? 'bg-green-500 dark:bg-green-500'
                  : 'bg-primary'
              )}
              style={{
                transform: `scaleX(${lineProgress / 100})`,
                transformOrigin: 'left',
              }}
            />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Main component - Minimal horizontal stepper with animated gradient progress
 */
export function PlanningProgress({ planningStatus }: PlanningProgressProps) {
  const { currentSubstep, substeps } = planningStatus;

  // Time tracking state for each phase
  const [storyStartTime, setStoryStartTime] = useState<number | null>(null);
  const [layoutStartTime, setLayoutStartTime] = useState<number | null>(null);

  if (!substeps) {
    return (
      <div className="text-sm text-gray-600 dark:text-gray-400">
        Initializing planning workflow...
      </div>
    );
  }

  // Get current phase
  const currentPhase = currentSubstep || 'story_architect';

  // Get active job ID for thinking display
  const activeJobId =
    substeps.storyArchitect.status === 'in_progress'
      ? substeps.storyArchitect.jobId
      : substeps.layoutDesigner.status === 'in_progress'
      ? substeps.layoutDesigner.jobId
      : undefined;

  // Use agent job hook to get thinking text
  const { job } = useAgentJob(activeJobId);

  // Track start time when phase becomes active
  useEffect(() => {
    if (substeps.storyArchitect.status === 'in_progress' && !storyStartTime) {
      setStoryStartTime(Date.now());
    }
  }, [substeps.storyArchitect.status, storyStartTime]);

  useEffect(() => {
    if (substeps.layoutDesigner.status === 'in_progress' && !layoutStartTime) {
      setLayoutStartTime(Date.now());
    }
  }, [substeps.layoutDesigner.status, layoutStartTime]);

  // Trigger re-render every second to update progress smoothly
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!activeJobId) return;

    const interval = setInterval(() => {
      setTick((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [activeJobId]);

  // Calculate current phase progress (0-100, capped at 95 until confirmed)
  const getCurrentPhaseProgress = (): number => {
    if (currentPhase === 'story_architect' && storyStartTime) {
      const elapsed = Math.floor((Date.now() - storyStartTime) / 1000);
      const estimated = ESTIMATED_DURATIONS.story_architect;
      return Math.min(95, (elapsed / estimated) * 100);
    }
    if (currentPhase === 'layout_designer' && layoutStartTime) {
      const elapsed = Math.floor((Date.now() - layoutStartTime) / 1000);
      const estimated = ESTIMATED_DURATIONS.layout_designer;
      return Math.min(95, (elapsed / estimated) * 100);
    }
    return 0;
  };

  // Calculate line progress for each connecting line
  // Line 1: Fills during story_architect phase
  const line1Progress =
    substeps.storyArchitect.status === 'completed'
      ? 100
      : currentPhase === 'story_architect'
      ? getCurrentPhaseProgress()
      : 0;

  // Line 2: Fills during layout_designer phase
  const line2Progress =
    substeps.layoutDesigner.status === 'completed'
      ? 100
      : currentPhase === 'layout_designer'
      ? getCurrentPhaseProgress()
      : 0;

  // Check if all complete
  const isComplete = substeps.initiateSlides.status === 'completed';

  // Get thinking text
  const thinkingText = job?.progress?.thinking;
  const agentName = job?.agentName;

  // Format agent name for display
  const getAgentDisplayName = (name?: string) => {
    if (name === 'story_architect') return 'Story Architect';
    if (name === 'layout_designer') return 'Layout Designer';
    return 'Processing';
  };

  return (
    <div className="space-y-2 p-3">
      {/* Header with thinking text */}
      <div className="space-y-1">
        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Creating Your Video Plan
        </h4>

        {/* Thinking text display */}
        {activeJobId && thinkingText && (
          <div className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
            <Loader2 className="h-3 w-3 animate-spin flex-shrink-0 mt-0.5" />
            <span>
              <span className="font-medium">{getAgentDisplayName(agentName)}: </span>
              {thinkingText}
            </span>
          </div>
        )}

        {/* Setup phase message (no job for this step) */}
        {!activeJobId && substeps.initiateSlides.status === 'in_progress' && (
          <div className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
            <Loader2 className="h-3 w-3 animate-spin flex-shrink-0 mt-0.5" />
            <span>Creating slide directories and preparing project structure...</span>
          </div>
        )}
      </div>

      {/* Horizontal Stepper with animated gradient lines */}
      <div className="flex items-center pt-1">
        <PhaseNode
          phase="story_architect"
          currentPhase={currentPhase}
          status={substeps.storyArchitect.status}
          lineProgress={line1Progress}
        />
        <PhaseNode
          phase="layout_designer"
          currentPhase={currentPhase}
          status={substeps.layoutDesigner.status}
          lineProgress={line2Progress}
        />
        <PhaseNode
          phase="initiate_slides"
          currentPhase={currentPhase}
          status={substeps.initiateSlides.status}
          isLast
        />
      </div>

      {/* Completion message */}
      {isComplete && (
        <div className="flex items-center gap-2 text-green-600 dark:text-green-400 pt-1">
          <CheckCircle2 className="h-4 w-4" />
          <span className="text-xs font-medium">Plan created successfully!</span>
        </div>
      )}
    </div>
  );
}
